import { DRESS_SECONDS_DEFAULT, DRESS_SECONDS_OPTIONS, THEMES, type JudgePersona, type ShowTheme } from '@runway/shared';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../net/api';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Modal';
import { Spinner } from '../../ui/Spinner';
import { toast } from '../../ui/Toast';
import { cx } from '../../ui/cx';
import { createShow, fetchJudges, isUnavailable } from './api';

// The judge cards carry a live 3D bust (three/R3F); load them only when the wizard reaches that step
// so the home shell does not pay for the 3D bundle.
const JudgeAvatarCard = lazy(() => import('../judges/JudgeAvatarCard').then((m) => ({ default: m.JudgeAvatarCard })));

const CUSTOM_THEME_MAX = 60;
const STEPS = ['Theme', 'Judge', 'Time'] as const;

type Props = { open: boolean; onClose: () => void };

function fmtSeconds(s: number): { big: string; small: string } {
  if (s < 60) return { big: `${s}`, small: 'seconds' };
  const m = s / 60;
  return { big: Number.isInteger(m) ? `${m}` : m.toFixed(1), small: m === 1 ? 'minute' : 'minutes' };
}

/** 3-step "Start a fashion show": theme → judge → dress-up time → POST /api/shows. */
export function StartShowWizard({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [themeId, setThemeId] = useState<string | null>(THEMES[0]?.id ?? null);
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [judges, setJudges] = useState<JudgePersona[] | null>(null);
  const [judgesError, setJudgesError] = useState<string | null>(null);
  const [judgeId, setJudgeId] = useState<string | null>(null);
  const [dressSeconds, setDressSeconds] = useState<number>(DRESS_SECONDS_DEFAULT);
  const [submitting, setSubmitting] = useState(false);

  const themeTitle = useCustom ? custom.trim() : THEMES.find((t) => t.id === themeId)?.title ?? '';
  const themeValid = useCustom ? custom.trim().length >= 2 && custom.trim().length <= CUSTOM_THEME_MAX : Boolean(themeId);
  const judge = judges?.find((j) => j.id === judgeId) ?? null;

  // Reset when reopened.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setSubmitting(false);
  }, [open]);

  // Load judges lazily the first time the modal opens.
  useEffect(() => {
    if (!open || judges !== null) return;
    let cancelled = false;
    setJudgesError(null);
    fetchJudges()
      .then((list) => {
        if (cancelled) return;
        setJudges(list);
        if (list.length && !judgeId) setJudgeId(list[Math.floor(Math.random() * list.length)]?.id ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setJudges([]);
        setJudgesError(isUnavailable(err) ? 'The judges are still getting ready backstage (the judges API is not up yet).' : err instanceof ApiError ? err.message : 'Could not load the judges');
      });
    return () => {
      cancelled = true;
    };
  }, [open, judges, judgeId]);

  const retryJudges = () => setJudges(null);

  const canNext = step === 0 ? themeValid : step === 1 ? Boolean(judgeId) : true;

  const submit = async () => {
    if (!themeValid || !judgeId) return;
    setSubmitting(true);
    try {
      // Send the catalog id for curated themes (the server resolves title + blurb for the judge); custom text as typed.
      const theme = useCustom ? custom.trim() : themeId ?? themeTitle;
      const { show, roomId } = await createShow({ theme, judgeId, dressSeconds });
      onClose();
      navigate(`/show/${show.id}`, { state: { roomId, host: true, show } });
    } catch (err) {
      toast.error(isUnavailable(err) ? 'Shows are not ready on the server yet — try again soon.' : err instanceof Error ? err.message : 'Could not start the show');
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Start a fashion show" size="lg" locked={submitting}
      footer={
        <>
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={submitting}>
              Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button variant="gold" onClick={() => setStep((s) => s + 1)} disabled={!canNext}>
              Next
            </Button>
          ) : (
            <Button variant="primary" size="lg" onClick={() => void submit()} loading={submitting} disabled={!themeValid || !judgeId}>
              Open the lobby ✨
            </Button>
          )}
        </>
      }
    >
      <Steps current={step} onJump={(i) => i < step && setStep(i)} />

      <div className="wizard-body">
        {step === 0 ? (
          <ThemeStep
            themeId={useCustom ? null : themeId}
            onPick={(t) => {
              setThemeId(t.id);
              setUseCustom(false);
            }}
            custom={custom}
            useCustom={useCustom}
            onCustom={(v) => {
              setCustom(v.slice(0, CUSTOM_THEME_MAX));
              setUseCustom(true);
            }}
            onFocusCustom={() => setUseCustom(true)}
          />
        ) : null}

        {step === 1 ? (
          <JudgeStep judges={judges} error={judgesError} judgeId={judgeId} onPick={(j) => setJudgeId(j.id)} onRetry={retryJudges} />
        ) : null}

        {step === 2 ? (
          <div className="stack">
            <div>
              <h3 style={{ marginBottom: 4 }}>How long to get dressed?</h3>
              <p className="help">Everyone dresses at the same time. When the timer ends (or everyone is ready) the runway starts.</p>
            </div>
            <div className="time-options" role="radiogroup" aria-label="Dress-up time">
              {DRESS_SECONDS_OPTIONS.map((s) => {
                const f = fmtSeconds(s);
                return (
                  <button key={s} type="button" className="time-option" role="radio" aria-checked={dressSeconds === s} aria-pressed={dressSeconds === s} onClick={() => setDressSeconds(s)}>
                    <span className="big">{f.big}</span>
                    <span className="small">{f.small}</span>
                  </button>
                );
              })}
            </div>
            <div className="wizard-summary" aria-label="Summary">
              <div>
                <div className="k">Theme</div>
                <div className="v">{themeTitle}</div>
              </div>
              <div>
                <div className="k">Judge</div>
                <div className="v">{judge?.name ?? '—'}</div>
              </div>
              <div>
                <div className="k">Dress-up</div>
                <div className="v">
                  {fmtSeconds(dressSeconds).big} {fmtSeconds(dressSeconds).small}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function Steps({ current, onJump }: { current: number; onJump: (i: number) => void }) {
  return (
    <ol className="wizard-steps" aria-label="Steps">
      {STEPS.map((label, i) => (
        <li key={label} style={{ display: 'contents' }}>
          <button
            type="button"
            className={cx('wizard-step', i < current && 'done', i === current && 'current')}
            onClick={() => onJump(i)}
            disabled={i >= current}
            style={{ background: 'none', border: 'none', padding: 0, cursor: i < current ? 'pointer' : 'default' }}
            aria-current={i === current ? 'step' : undefined}
          >
            <span className="num" aria-hidden="true">
              {i < current ? '✓' : i + 1}
            </span>
            {label}
          </button>
          {i < STEPS.length - 1 ? <span className="wizard-step-line" aria-hidden="true" /> : null}
        </li>
      ))}
    </ol>
  );
}

function ThemeStep({
  themeId,
  onPick,
  custom,
  useCustom,
  onCustom,
  onFocusCustom,
}: {
  themeId: string | null;
  onPick: (t: ShowTheme) => void;
  custom: string;
  useCustom: boolean;
  onCustom: (v: string) => void;
  onFocusCustom: () => void;
}) {
  return (
    <div className="stack">
      <div>
        <h3 style={{ marginBottom: 4 }}>Pick a theme</h3>
        <p className="help">The judge scores every look against it. Choose one, or make up your own.</p>
      </div>
      <div className="theme-grid" role="listbox" aria-label="Themes">
        {THEMES.map((t) => (
          <button key={t.id} type="button" className="theme-card" role="option" aria-selected={themeId === t.id} aria-pressed={themeId === t.id} onClick={() => onPick(t)}>
            <span className="emoji" aria-hidden="true">
              {t.emoji}
            </span>
            <span className="title">{t.title}</span>
            <span className="blurb">{t.blurb}</span>
          </button>
        ))}
        <label className="theme-custom" data-active={useCustom}>
          <span aria-hidden="true" style={{ fontSize: 22 }}>
            ✏️
          </span>
          <input
            className="input"
            value={custom}
            onChange={(e) => onCustom(e.target.value)}
            onFocus={onFocusCustom}
            placeholder="Your own theme, e.g. “Dragon’s birthday party”"
            maxLength={CUSTOM_THEME_MAX}
            aria-label="Custom theme"
          />
          <span className="small muted" style={{ flexShrink: 0 }}>
            {custom.length}/{CUSTOM_THEME_MAX}
          </span>
        </label>
      </div>
    </div>
  );
}

type ProviderFilter = 'all' | 'anthropic' | 'openai';
const PROVIDER_FILTERS: Array<{ id: ProviderFilter; label: string }> = [
  { id: 'all', label: 'Everyone' },
  { id: 'anthropic', label: 'Claude' },
  { id: 'openai', label: 'GPT' },
];

function JudgeStep({ judges, error, judgeId, onPick, onRetry }: { judges: JudgePersona[] | null; error: string | null; judgeId: string | null; onPick: (j: JudgePersona) => void; onRetry: () => void }) {
  const [provider, setProvider] = useState<ProviderFilter>('all');
  const providers = useMemo(() => new Set((judges ?? []).map((j) => j.provider)), [judges]);
  const sorted = useMemo(
    () =>
      (judges ?? [])
        .filter((j) => provider === 'all' || j.provider === provider)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [judges, provider],
  );
  return (
    <div className="stack">
      <div>
        <h3 style={{ marginBottom: 4 }}>Choose your judge</h3>
        <p className="help">Each judge has their own taste — and their own opinions. Pick who gets the final say.</p>
      </div>
      {providers.size > 1 ? (
        <div className="row" role="group" aria-label="Filter judges by model family" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          {PROVIDER_FILTERS.map((f) => (
            <Button key={f.id} size="sm" variant={provider === f.id ? 'primary' : 'ghost'} aria-pressed={provider === f.id} onClick={() => setProvider(f.id)}>
              {f.label}
            </Button>
          ))}
        </div>
      ) : null}
      {judges === null ? (
        <div className="empty">
          <Spinner size={28} />
          <p>Calling the judges…</p>
        </div>
      ) : error ? (
        <div className="empty" role="alert">
          <div className="empty-emoji" aria-hidden="true">
            🎤
          </div>
          <h4>No judges yet</h4>
          <p>{error}</p>
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : sorted.length === 0 ? (
        <div className="empty">
          <div className="empty-emoji" aria-hidden="true">
            🎤
          </div>
          <h4>The judges’ table is empty</h4>
          <p>No AI judge is configured on the server yet. Ask a grown-up to add an API key.</p>
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Check again
          </Button>
        </div>
      ) : (
        <Suspense
          fallback={
            <div className="empty" role="status" aria-live="polite">
              <Spinner size={28} />
              <p>Seating the judges…</p>
            </div>
          }
        >
          <div className="judge-grid" role="listbox" aria-label="Judges">
            {sorted.map((j) => (
              <JudgeAvatarCard key={j.id} judge={j} selected={j.id === judgeId} onSelect={onPick} size="sm" />
            ))}
          </div>
        </Suspense>
      )}
    </div>
  );
}
